import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { LogIn } from 'lucide-react';
import { api } from '@/lib/api';
import { loginScopes } from './msal';
import { setAuthTokenProvider } from './token';
import { useAppStore } from '@/store/app';
export function AuthGate({ children }) {
    const { instance, accounts, inProgress } = useMsal();
    const setAuthToken = useAppStore((state) => state.setAuthToken);
    const currentUser = useAppStore((state) => state.currentUser);
    const setCurrentUser = useAppStore((state) => state.setCurrentUser);
    const [error, setError] = useState(null);
    const [checkedAccess, setCheckedAccess] = useState(false);
    const reauthStartedRef = useRef(false);
    const account = accounts[0] ?? null;
    const acquireToken = useCallback(async () => {
        if (!account)
            return null;
        try {
            const result = await instance.acquireTokenSilent({ account, scopes: loginScopes });
            return result.idToken || result.accessToken || null;
        }
        catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                await instance.acquireTokenRedirect({ account, scopes: loginScopes });
                return null;
            }
            throw err;
        }
    }, [account, instance]);
    useEffect(() => {
        if (!account) {
            setAuthTokenProvider(null);
            setAuthToken(null);
            setCurrentUser(null);
            setCheckedAccess(false);
            return;
        }
        setAuthTokenProvider(acquireToken);
        let cancelled = false;
        const refresh = async () => {
            try {
                const token = await acquireToken();
                if (!cancelled) {
                    setAuthToken(token);
                    const user = await api.me();
                    if (cancelled)
                        return;
                    setCurrentUser(user);
                    setCheckedAccess(true);
                    setError(null);
                }
            }
            catch (err) {
                if (!cancelled) {
                    const status = err.status;
                    setCurrentUser(null);
                    setCheckedAccess(true);
                    if (status === 401) {
                        setCheckedAccess(false);
                        setError('Your session expired. Signing in again.');
                        if (!reauthStartedRef.current && inProgress === 'none') {
                            reauthStartedRef.current = true;
                            await instance.loginRedirect({ scopes: loginScopes, prompt: 'login' });
                        }
                    }
                    else if (status === 403) {
                        setError(null);
                    }
                    else {
                        setError(err.message || 'Unable to verify access');
                    }
                }
            }
        };
        void refresh();
        const timer = window.setInterval(refresh, 5 * 60 * 1000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            setAuthTokenProvider(null);
        };
    }, [account, acquireToken, inProgress, instance, setAuthToken, setCurrentUser]);
    const login = () => {
        setError(null);
        void instance.loginRedirect({ scopes: loginScopes });
    };
    const logout = () => {
        setAuthTokenProvider(null);
        setAuthToken(null);
        setCurrentUser(null);
        void instance.logoutRedirect({ account: account ?? undefined });
    };
    if (!account) {
        return (_jsx("main", { className: "auth-shell", children: _jsxs("section", { className: "auth-panel", children: [_jsx("div", { className: "auth-brand", children: "EY Video Analyzer" }), _jsx("h1", { children: "Sign in with EY SSO" }), _jsx("p", { children: "Use your EY account to access the video analysis workspace." }), error && _jsx("div", { className: "auth-error", children: error }), _jsxs("button", { className: "btn btn-primary", type: "button", onClick: login, disabled: inProgress !== 'none', children: [_jsx(LogIn, { size: 16 }), "Sign in"] })] }) }));
    }
    if (!checkedAccess) {
        return (_jsx("main", { className: "auth-shell", children: _jsxs("section", { className: "auth-panel", children: [_jsx("div", { className: "auth-brand", children: "EY Video Analyzer" }), _jsx("h1", { children: "Checking access" }), _jsx("p", { children: "Verifying your EYVA role assignment." })] }) }));
    }
    if (!currentUser?.is_user) {
        return (_jsx("main", { className: "auth-shell", children: _jsxs("section", { className: "auth-panel", children: [_jsx("div", { className: "auth-brand", children: "EY Video Analyzer" }), _jsx("h1", { children: "Access pending" }), _jsx("p", { children: "Your account is authenticated but has not been approved for EYVA access." }), error && _jsx("div", { className: "auth-error", children: error }), _jsx("button", { className: "btn btn-secondary", type: "button", onClick: logout, children: "Sign out" })] }) }));
    }
    return _jsx(_Fragment, { children: children });
}
